import getAxiosFactory from "./customAxios";
import config from "../config";
import { msGraphAuthService as authService } from "./MsGraphAuthService";
import settings from "../appSettings";
import { TFunction } from "i18next";
import { CalendarEvent } from "../@types/graph";

const CALENDAR_CONNECT_KEY = "pinnacle_calendar_connect_provider";
const CALENDAR_ORIGINAL_ACCOUNT_KEY = "pinnacle_calendar_original_account";
const CALENDAR_KNOWN_ACCOUNTS_KEY = "pinnacle_calendar_known_accounts";
const CALENDAR_INCLUDE_PRIVATE_KEY = "pinnacle_calendar_include_private_events";
const GOOGLE_CALENDAR_SCOPE =
  "https://www.googleapis.com/auth/calendar.readonly";

export interface CalendarConnectionResponse {
  provider: string;
  calendarId: string;
  calendarName?: string;
  accountUsername?: string;
  connectedAt?: Date;
  includePrivateEvents?: boolean;
}

export interface CalendarStatusResponse {
  connected: boolean;
  connections: CalendarConnectionResponse[];
}

export interface CalendarDisconnectResponse {
  disconnected: boolean;
  remainingConnections: CalendarConnectionResponse[];
}

type GraphErrorPayload = {
  error?: {
    code?: string;
    message?: string;
  };
};

const parseGraphErrorPayload = (
  rawMessage: string,
): GraphErrorPayload["error"] | undefined => {
  const jsonStart = rawMessage.indexOf("{");
  if (jsonStart < 0) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(rawMessage.slice(jsonStart)) as GraphErrorPayload;
    return parsed.error;
  } catch {
    return undefined;
  }
};

export const getCalendarErrorMessage = (
  error: unknown,
  t: TFunction,
): string => {
  const fallback = t("calendar.connectError");
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "";

  const graphError = parseGraphErrorPayload(rawMessage);
  if (graphError?.code === "MailboxNotEnabledForRESTAPI") {
    return t("calendar.outlookMailboxNotEnabled");
  }

  return rawMessage || fallback;
};

// ────────────────────────────────────────────────────
//  Status
// ────────────────────────────────────────────────────

export const getCalendarStatus = async (): Promise<CalendarStatusResponse> => {
  const factory = await getAxiosFactory(
    config.api.baseUrl,
    "/calendar/status",
  )();
  const response = await factory.request<CalendarStatusResponse>({
    method: "GET",
  });
  return response.data;
};

// ────────────────────────────────────────────────────
//  Outlook — redirect-based connect flow
// ────────────────────────────────────────────────────

export const initiateCalendarConnectRedirect = async (
  provider: string,
  includePrivateEvents: boolean,
): Promise<void> => {
  const msalInstance = authService.getMsalInstance();
  const originalAccount = authService.getActiveAccount();
  sessionStorage.setItem(CALENDAR_CONNECT_KEY, provider);
  sessionStorage.setItem(
    CALENDAR_ORIGINAL_ACCOUNT_KEY,
    originalAccount?.username ?? "",
  );
  sessionStorage.setItem(
    CALENDAR_INCLUDE_PRIVATE_KEY,
    JSON.stringify(includePrivateEvents),
  );
  // Save all known accounts so we can identify the new one after redirect
  const knownAccounts = authService
    .getAllAccounts()
    .map((a) => a.username.toLowerCase());
  sessionStorage.setItem(
    CALENDAR_KNOWN_ACCOUNTS_KEY,
    JSON.stringify(knownAccounts),
  );
  await msalInstance.acquireTokenRedirect({
    scopes: ["https://graph.microsoft.com/Calendars.Read"],
    authority: "https://login.microsoftonline.com/common",
    prompt: "select_account",
  });
};

/**
 * Non-destructive check for a pending calendar connect redirect.
 * Use this to inspect the flag without consuming it (getPendingCalendarConnect
 * reads AND deletes the sessionStorage keys in one shot).
 */
export const hasPendingCalendarConnect = (): boolean => {
  return !!sessionStorage.getItem(CALENDAR_CONNECT_KEY);
};

export const getPendingCalendarConnect = (): {
  provider: string;
  originalUsername: string;
  knownAccounts: string[];
  includePrivateEvents: boolean;
} | null => {
  const provider = sessionStorage.getItem(CALENDAR_CONNECT_KEY);
  const originalUsername =
    sessionStorage.getItem(CALENDAR_ORIGINAL_ACCOUNT_KEY) ?? "";
  const knownAccountsStr = sessionStorage.getItem(CALENDAR_KNOWN_ACCOUNTS_KEY);
  const knownAccounts: string[] = knownAccountsStr
    ? JSON.parse(knownAccountsStr)
    : [];
  const includePrivateRaw = sessionStorage.getItem(CALENDAR_INCLUDE_PRIVATE_KEY);
  const includePrivateEvents: boolean =
    includePrivateRaw !== null ? JSON.parse(includePrivateRaw) : true;
  if (provider) {
    sessionStorage.removeItem(CALENDAR_CONNECT_KEY);
    sessionStorage.removeItem(CALENDAR_ORIGINAL_ACCOUNT_KEY);
    sessionStorage.removeItem(CALENDAR_KNOWN_ACCOUNTS_KEY);
    sessionStorage.removeItem(CALENDAR_INCLUDE_PRIVATE_KEY);
    return { provider, originalUsername, knownAccounts, includePrivateEvents };
  }
  return null;
};

export const completeCalendarConnect = async (
  provider: string,
  graphAccessToken: string,
  accountUsername: string,
  includePrivateEvents: boolean,
): Promise<CalendarConnectionResponse> => {
  // Use the auth service to fetch calendar info
  const cal = await authService.fetchCalendar(graphAccessToken);

  const factory = await getAxiosFactory(
    config.api.baseUrl,
    `/calendar/connect/${provider}`,
  )();
  const response = await factory.request<CalendarConnectionResponse>({
    method: "POST",
    data: { calendarId: cal.id, calendarName: cal.name, accountUsername, includePrivateEvents },
  });
  return response.data;
};

// ────────────────────────────────────────────────────
//  Google — popup-based connect flow (GIS auth-code)
// ────────────────────────────────────────────────────

let gisScriptLoaded = false;

const loadGisScript = (): Promise<void> => {
  if (gisScriptLoaded) return Promise.resolve();
  return new Promise((resolve, reject) => {
    if (
      document.querySelector('script[src*="accounts.google.com/gsi/client"]')
    ) {
      gisScriptLoaded = true;
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = () => {
      gisScriptLoaded = true;
      resolve();
    };
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity Services script"));
    document.head.appendChild(script);
  });
};

const requestGoogleAuthCode = (): Promise<string> => {
  const clientId = settings.googleClientId;
  if (!clientId) {
    return Promise.reject(
      new Error(
        "Google Client ID is not configured. Set REACT_APP_GOOGLE_CLIENT_ID in your environment.",
      ),
    );
  }
  return new Promise((resolve, reject) => {
    const client = google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: GOOGLE_CALENDAR_SCOPE,
      ux_mode: "popup",
      callback: (response) => {
        if (response.code) {
          resolve(response.code);
        } else {
          reject(new Error("No authorization code received from Google"));
        }
      },
      error_callback: (error) =>
        reject(new Error(error.message || error.type || "Google auth failed")),
    });
    client.requestCode();
  });
};

export const connectGoogleCalendar =
  async (includePrivateEvents: boolean): Promise<CalendarConnectionResponse> => {
    await loadGisScript();
    const authCode = await requestGoogleAuthCode();

    const factory = await getAxiosFactory(
      config.api.baseUrl,
      "/calendar/connect/google",
    )();
    try {
      const response = await factory.request<CalendarConnectionResponse>({
        method: "POST",
        data: { authCode, includePrivateEvents },
      });
      return response.data;
    } catch (err: any) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Google calendar connection failed";
      throw new Error(msg);
    }
  };

// ────────────────────────────────────────────────────
//  Unified connect / disconnect
// ────────────────────────────────────────────────────

/**
 * Connect a calendar provider.
 * - Outlook: triggers MSAL redirect (page reloads).
 * - Google: opens GIS popup, returns when done.
 */
export const connectCalendar = async (
  provider: string,
  includePrivateEvents: boolean,
): Promise<CalendarConnectionResponse | never> => {
  if (provider === "google") {
    return connectGoogleCalendar(includePrivateEvents);
  }
  // Outlook — redirect flow (never returns in current tab)
  await initiateCalendarConnectRedirect(provider, includePrivateEvents);
  throw new Error("Redirect in progress");
};

/**
 * Disconnect a specific calendar account.
 * If accountUsername is provided, only that account is removed for the given provider.
 */
export const disconnectCalendar = async (
  provider: string,
  accountUsername?: string,
): Promise<CalendarDisconnectResponse> => {
  const params = accountUsername
    ? `?accountUsername=${encodeURIComponent(accountUsername)}`
    : "";
  const factory = await getAxiosFactory(
    config.api.baseUrl,
    `/calendar/disconnect/${provider}${params}`,
  )();
  const response = await factory.request<CalendarDisconnectResponse>({
    method: "DELETE",
  });

  // Remove the disconnected account from MSAL cache so it doesn't
  // interfere with future calendar connections.
  if (accountUsername && provider === "outlook") {
    const staleAccount = authService.getCalendarAccount(accountUsername);
    if (staleAccount) {
      await authService.removeAccount(staleAccount);
    }
  }

  return response.data;
};

// ────────────────────────────────────────────────────
//  Event fetching — Outlook (frontend Graph) + Google (backend-mediated)
// ────────────────────────────────────────────────────

/**
 * Fetch Outlook calendar events using the centralized auth service
 * No longer uses forceRefresh - lets MSAL handle token caching efficiently
 */
export const fetchOutlookCalendarEvents = async (
  accountUsername: string,
  startDateTime?: Date,
  endDateTime?: Date,
): Promise<CalendarEvent[]> => {
  const calendarAccount = authService.getCalendarAccount(accountUsername);

  if (!calendarAccount) {
    throw new Error(
      `Calendar account ${accountUsername} not found in MSAL cache. Please reconnect your calendar.`,
    );
  }

  // Use centralized auth service for event fetching with proper types
  return await authService.fetchCalendarEvents(
    calendarAccount,
    startDateTime,
    endDateTime,
  );
};

/** Keep backward-compatible alias */
export const fetchCalendarEvents = fetchOutlookCalendarEvents;

/**
 * Fetch Google Calendar events via the backend (which uses the stored refresh token).
 */
export const fetchGoogleCalendarEvents = async (
  accountUsername: string,
  startDateTime?: Date,
  endDateTime?: Date,
): Promise<any[]> => {
  const params = new URLSearchParams({
    accountUsername,
  });
  if (startDateTime) {
    params.set("startDateTime", startDateTime.toISOString());
  }
  if (endDateTime) {
    params.set("endDateTime", endDateTime.toISOString());
  }

  const factory = await getAxiosFactory(
    config.api.baseUrl,
    `/calendar/events/google?${params.toString()}`,
  )();
  const response = await factory.request<{ events: any[] }>({
    method: "GET",
  });
  return response.data.events ?? [];
};
